// Pure date-range math for the dashboard header + KPI comparisons.
// No Firestore here — just resolves a rangeKey (or custom {from, to}) into
// { start, end, prevStart, prevEnd } Date objects for filtering/comparison.

export const DATE_RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "12m", label: "12 months" },
  { key: "custom", label: "Custom" },
]

const startOfDay = (d) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

const endOfDay = (d) => {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

const getPrevBounds = (start, end) => {
  const spanMs = end.getTime() - start.getTime()
  const prevEnd = new Date(start.getTime() - 1)
  const prevStart = new Date(prevEnd.getTime() - spanMs)
  return { prevStart, prevEnd }
}

// Returns { start, end, prevStart, prevEnd } as Date objects.
// prevStart/prevEnd is the immediately preceding period of equal length.
export const getRangeBounds = (rangeKey, customRange) => {
  const now = new Date()
  const end = endOfDay(now)
  let start

  switch (rangeKey) {
    case "today":
      start = startOfDay(now)
      break
    case "7d":
      start = startOfDay(new Date(now.getTime() - 6 * 86400000))
      break
    case "90d":
      start = startOfDay(new Date(now.getTime() - 89 * 86400000))
      break
    case "12m": {
      const d = new Date(now)
      d.setMonth(d.getMonth() - 12)
      start = startOfDay(d)
      break
    }
    case "custom":
      if (customRange?.from && customRange?.to) {
        const customStart = startOfDay(new Date(customRange.from))
        const customEnd = endOfDay(new Date(customRange.to))
        return { start: customStart, end: customEnd, ...getPrevBounds(customStart, customEnd) }
      }
      start = startOfDay(new Date(now.getTime() - 29 * 86400000))
      break
    case "30d":
    default:
      start = startOfDay(new Date(now.getTime() - 29 * 86400000))
      break
  }

  return { start, end, ...getPrevBounds(start, end) }
}

// Normalizes Firestore Timestamp, ISO string, Date, or number -> millis (or null)
export const toMillis = (value) => {
  if (!value) return null
  if (typeof value?.toMillis === "function") return value.toMillis()
  if (value instanceof Date) return value.getTime()
  if (typeof value === "number") return value
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

export const isWithin = (value, start, end) => {
  const ms = toMillis(value)
  if (ms === null) return false
  return ms >= start.getTime() && ms <= end.getTime()
}

// % change, safe against divide-by-zero. Returns null when there's no
// previous-period baseline (renders as no badge rather than a fake number).
export const percentChange = (current, previous) => {
  if (!previous) return current > 0 ? null : 0
  return Math.round(((current - previous) / previous) * 1000) / 10
}

export const dateKeyFor = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

// Builds ordered day/month buckets for the chart's x-axis, with start/end
// bounds per bucket for anything that still needs a Date range instead of a key.
export const buildBuckets = (rangeKey) => {
  const now = new Date()

  if (rangeKey === "12m") {
    const buckets = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      buckets.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleString("en-US", { month: "short" }).toUpperCase(),
        start: new Date(d.getFullYear(), d.getMonth(), 1),
        end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999),
      })
    }
    return buckets
  }

  const days = rangeKey === "7d" ? 7 : rangeKey === "90d" ? 90 : 30
  const buckets = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    d.setHours(0, 0, 0, 0)
    const end = new Date(d)
    end.setHours(23, 59, 59, 999)
    buckets.push({
      key: dateKeyFor(d),
      label: days <= 7 ? d.toLocaleDateString("en-US", { weekday: "short" }) : `${d.getMonth() + 1}/${d.getDate()}`,
      start: d,
      end,
    })
  }
  return buckets
}